import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {
    ElapsedClockCounter, WaterMarkView,
    WaterMarkViewDesktop,
    WaterMarkViewMobile
} from '@gongxigames/framework/slot/modules/watermark';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, CurrencyFormatter, inject, LanguageService, Rectangle, Timer} from '@gongxigames/framework/core';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {getCurrentTime} from '@gongxigames/framework/slot/utils';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {ILogic} from '@gongxigames/framework/slot/types';
import {ILogicPromotionFunctional} from '@gongxigames/framework/promotions';

export class WaterMarkModule extends Module<WaterMarkView, GlobalEventsMapForBackwardCompatibility> {
    public static NAME = Symbol('WaterMarkModule');
    private sessionBets = 0;
    private sessionEarnings = 0;
    private sessionNetBalance = 0;
    private clockTimer?: Timer;
    private elapsedClockCounter: ElapsedClockCounter = new ElapsedClockCounter();
    private actionNumberForSessionEarnings = -1;

    @inject(CoreDI.CurrencyFormatter)
    protected currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.LanguageService)
    protected languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected gameConfig!: GameConfig;

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.on('General.UI.BALANCE_FIELD.UPDATED', this.onBalanceFieldUpdated.bind(this));
    }

    protected setupViews(): void {
        super.setupViews();

        const view = this.isPlatformDesktop() ? WaterMarkViewDesktop.NAME : WaterMarkViewMobile.NAME;

        this.setView(view);
        this.view.initProperties({
            gameName: this.getGameName(),
            isGlobalClockEnabled: this.settings.get('clock'),
            isNetPositionEnabled: this.settings.get('netPositionEnabled'),
            isElapsedTimeEnabled: this.settings.get('elapsedTimeEnabled'),
            isSessionEarningsEnabled: this.settings.get('sessionEarningsEnabled'),
            isSessionBetsEnabled: this.settings.get('sessionBetsEnabled'),
            isShowMaxWinProbabilityEnabled: this.settings.get('showMaxWinProbabilityOnUI'),
            isRTPWatermarkEnabled: this.settings.get('showRTPWatermark')
        });
    }

    protected onGameStarted(): void {
        this.elapsedClockCounter.init(Date.now(), this._eventManager);
        this.clockTick();
        this.updateSessionBetLabel();
        this.updateSessionEarnings();
        this.updateSessionNetBalance();
        this.updateMaxWinProbability(this.gameConfig.winProbability);
        this.updateRTPWatermark(this.gameConfig.RTP);
        this.view.show();
        this.on('General.UI.BALANCE.UPDATED', this.onBalanceUpdated.bind(this));
        this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onReelsSpinningStarted.bind(this));

        strictUiEmitter.on('UI.menu.opened', this.setMenuBarOpen.bind(this, true));
        strictUiEmitter.on('UI.menu.menuBarClosed', this.setMenuBarOpen.bind(this, false));
    }

    protected clockTick(): void {
        this.updateClock();
        this.clockTimer = this.updateManager.addTimer(this.clockTick.bind(this), 1000, true);
    }

    protected updateClock(): void {
        this.view.updateGlobalClock(getCurrentTime());

        if (!this.elapsedClockCounter.isPaused) {
            this.view.updateElapsedTimeClock(this.elapsedClockCounter.getTime());
        }
    }

    protected onReelsSpinningStarted(): void {
        this.updateSessionBet();
    }

    protected onBalanceUpdated(id: symbol | string, balance: number): void {
        this.updateSessionEarnings();
        this.updateSessionNetBalance();
    }

    protected onBalanceFieldUpdated(id: symbol | string, bounds: Rectangle): void {
        this.view.updateBalanceFieldBounds(bounds);
    }

    protected updateSessionBet(): void {
        const isOnPromotions: boolean = this.getLogic<ILogic & ILogicPromotionFunctional>().getPromotion().isActive();

        if (this.logic.nextAction === Action.SPIN && !isOnPromotions) {
            this.sessionBets += this.logic.getTotalSessionBet();
            this.updateSessionBetLabel();
        }
    }

    protected updateSessionBetLabel(): void {
        this.view.updateSessionBet(this.currencyFormatter.formatCents(this.sessionBets));
    }

    protected addSessionEarnings(): void {
        const actionNumber = this.logic.getActionNumber();

        if (this.logic.nextActionIsSpin() && this.actionNumberForSessionEarnings !== actionNumber) {
            this.sessionEarnings += this.logic.getTotalWin();
            this.actionNumberForSessionEarnings = actionNumber;
        }
    }

    protected updateSessionEarnings(): void {
        this.addSessionEarnings();
        this.view.updateSessionEarnings(this.currencyFormatter.formatCents(this.sessionEarnings));
    }

    protected updateSessionNetBalance(): void {
        this.sessionNetBalance = this.sessionEarnings - this.sessionBets;

        this.view.updateSessionBalance(this.currencyFormatter.formatCents(this.sessionNetBalance));
    }

    protected updateMaxWinProbability(value: string): void {
        this.view.updateMaxWinProbability(value);
    }

    protected updateRTPWatermark(value: number): void {
        const rtp = this.currencyFormatter.formatNumber(value);

        this.view.updateRTPWatermark(rtp);
    }

    protected setMenuBarOpen(open: boolean): void {
        this.view?.setMenuBarOffsets(open);
    }

    protected getGameName(): string {
        const localizeGameName = this.languageService.get('gameName');
        let str = localizeGameName === 'undefined' ? this.gameConfig.gameName : localizeGameName;

        if ((this.settings.get('offlineMode') || this.settings.get('funMode')) && !this.settings.get('hideDemoMark')) {
            const demoPrefix = this.languageService.get('watermarkDemoTag');

            str = `${str} (${demoPrefix})`;
        }

        return str.toUpperCase();
    }
}
